/** Image URL builder — generates optimized Supabase Storage URLs with resizing and format params */

const SUPABASE_PROJECT_URL = import.meta.env.VITE_SUPABASE_URL;

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif';
  resize?: 'cover' | 'contain' | 'fill';
}

/** Image usage context — determines optimal breakpoints and quality */
export type ImagePreset = 'hero' | 'card' | 'thumbnail' | 'avatar';

/** Breakpoints & quality for each preset */
const PRESETS: Record<ImagePreset, { breakpoints: number[]; quality: number; maxWidth: number }> = {
  hero: { breakpoints: [480, 768, 1200, 1920], quality: 85, maxWidth: 1920 },
  card: { breakpoints: [320, 500, 768], quality: 80, maxWidth: 768 },
  thumbnail: { breakpoints: [150, 320], quality: 70, maxWidth: 320 },
  avatar: { breakpoints: [64, 128, 256], quality: 70, maxWidth: 256 },
};

/** Generate responsive srcSet for a given preset */
export function getImageSrcSet(path: string, bucket: string, preset: ImagePreset): string {
  const cfg = PRESETS[preset];
  if (!cfg) return '';
  return cfg.breakpoints
    .map(
      (w) => `${getSupabaseImageUrl(path, bucket, { width: w, format: 'webp', quality: cfg.quality })} ${String(w)}w`
    )
    .join(', ');
}

/** Get the fallback (largest) image URL for a preset */
export function getImageFallbackUrl(path: string, bucket: string, preset: ImagePreset): string {
  const cfg = PRESETS[preset];
  if (!cfg) return '';
  return getSupabaseImageUrl(path, bucket, {
    width: cfg.maxWidth,
    format: 'webp',
    quality: cfg.quality,
  });
}

/**
 * Returns an optimized image URL via Supabase's Render API.
 * External URLs are returned as-is.
 */
export function getSupabaseImageUrl(path: string, bucket = 'public', options: ImageTransformOptions = {}): string {
  if (!path) return '';

  const { width, height, quality = 80, format = 'webp', resize = 'cover' } = options;

  // External URLs, absolute paths, and data URIs — return as-is
  if (path.startsWith('http') || path.startsWith('/') || path.startsWith('data:')) {
    return path;
  }

  const baseUrl = `${SUPABASE_PROJECT_URL}/storage/v1/render/image/v1/${bucket}/${path}`;
  const params = new URLSearchParams();

  params.append('quality', quality.toString());
  params.append('format', format);
  if (width) params.append('width', width.toString());
  if (height) params.append('height', height.toString());
  if (resize) params.append('resize', resize);

  return `${baseUrl}?${params.toString()}`;
}
